import {Injectable} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {map} from "rxjs/operators";
import {Apollo} from "apollo-angular";
import {
  DeploymentGqlModel,
  DeploymentListGqlModel,
  DeploymentMutateGqlModel,
  DeploymentRemoveGqlModel,
  DeploymentUnRejectGqlModel,
  DeploymentUpdateGqlModel,
  SiteForDeploymentGqlModel,
} from "./deployment.gql-model";
import {sequenceAsync} from "../misc/sequence.decorator";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Injectable()
export class DeploymentService {
  private url: string = "/api/deployment";

  constructor(private http: HttpClient, private apollo: Apollo) {}

  @sequenceAsync()
  getDeployments(
    page: number = DEFAULT_PAGE,
    limit: number = DEFAULT_PAGE_LIMIT,
    search: string = null,
    id: string = null,
    siteId: string = null,
    projectId: string = null,
    filterOutComplete: boolean = false,
    searchSerialNumber: boolean = false,
  ): Promise<DeploymentListGqlModel> {
    const variables = DeploymentListGqlModel.variables({
      projectId,
      siteId,
      id,
      search,
      page,
      limit,
      filterOutComplete,
      searchSerialNumber,
    });
    const query = DeploymentListGqlModel.query;

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(DeploymentListGqlModel.mapper)
      .toPromise();
  }

  getDeploymentById(id: string = null): Promise<DeploymentGqlModel> {
    const variables = DeploymentGqlModel.variables(id);
    const query = DeploymentGqlModel.query;

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(DeploymentGqlModel.mapper)
      .toPromise();
  }

  saveDeployment(deployment: DeploymentGqlModel): Promise<DeploymentGqlModel> {
    const deploymentInput = new DeploymentUpdateGqlModel(deployment);
    const model = new DeploymentMutateGqlModel(deploymentInput);

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(DeploymentMutateGqlModel.mapper)
      .toPromise();
  }

  createDeployment(name: string, projectId: string, siteId: string = null): Promise<DeploymentGqlModel> {
    const deployment = new DeploymentGqlModel({name, projectId, siteId});

    return this.saveDeployment(deployment);
  }

  remove(id: string): Promise<DeploymentGqlModel> {
    const deploymentInput = new DeploymentRemoveGqlModel(id);
    const model = new DeploymentMutateGqlModel(deploymentInput);

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(DeploymentMutateGqlModel.mapper)
      .toPromise();
  }

  unRejectDeployment(id: string): Promise<DeploymentGqlModel> {
    const deploymentInput = new DeploymentUnRejectGqlModel(id);
    const model = new DeploymentMutateGqlModel(deploymentInput);

    return this.apollo.mutate({mutation: model.mutation, variables: model.variables, fetchPolicy: "no-cache"})
      .pipe(DeploymentMutateGqlModel.mapper)
      .toPromise();
  }

  getSitesEligibleForDeployment(projectId: string): Promise<SiteForDeploymentGqlModel[]> {
    const variables = SiteForDeploymentGqlModel.variables(projectId);
    const query = SiteForDeploymentGqlModel.query;

    return this.apollo.query({query, variables, fetchPolicy: "no-cache"})
      .pipe(SiteForDeploymentGqlModel.mapper)
      .toPromise();
  }

  sendDeploymentToInstallPartner(deploymentId, projectId, accountId): Promise<{success: boolean, message: string}> {
    const params = {
      deploymentId,
      projectId,
      accountId,
    };

    return this.http.post<{success: boolean, message: string}>(`${this.url}/send-deployment-install-partner`, params)
      .toPromise();
  }

  validateProjectAllowedInstallPartner(projectId): Promise<boolean> {
    return this.http.get<{valid: boolean}>(`${this.url}/check-project-allowed-install-partner`, {params: {projectId}})
      .pipe(map(json => !!json["valid"]))
      .toPromise();
  }
}
